import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Circle, ShieldCheck } from "lucide-react";
import { num } from "@/lib/format";

export const LOGO_URL = "https://customer-assets-jai6qajn.emergentagent.net/job_boleto-hub/artifacts/r50kn4k6_3D1D9A43-ED09-4557-9CAB-9CE375D6BE32.png";

export const Logo = ({ dark = false, size = "md", markOnly = false }) => {
  const s = size === "lg" ? "h-12 w-12" : "h-10 w-10";
  return (
    <div className="flex items-center gap-2.5" data-testid="brand-logo">
      <img src={LOGO_URL} alt="Círculo Dorado" className={`${s} shrink-0 rounded-full object-cover gold-ring`} />
      {!markOnly && (
        <div className="leading-none">
          <div className={`font-display font-bold tracking-tight ${dark ? "text-white" : "text-slate-900"} ${size === "lg" ? "text-xl" : "text-base"}`}>
            Círculo <span className={dark ? "gold-text" : "text-brand"}>Dorado</span>
          </div>
          <div className={`mt-0.5 text-[9px] font-semibold uppercase tracking-[0.22em] ${dark ? "text-brand-bright/70" : "text-slate-400"}`}>Rifas y Sorteos</div>
        </div>
      )}
    </div>
  );
};

export const Section = ({ children, className = "", id }) => (
  <section id={id} className={`mx-auto w-full max-w-7xl px-5 sm:px-8 ${className}`}>{children}</section>
);

export const Eyebrow = ({ children }) => (
  <span className="inline-flex items-center gap-2 rounded-full border border-brand/30 bg-brand/5 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-brand">
    {children}
  </span>
);

export const StatusBadge = ({ label, cls }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${cls}`}>{label}</span>
);

export const ProgressBar = ({ value, className = "" }) => (
  <div className={`h-2.5 w-full overflow-hidden rounded-full bg-slate-100 ${className}`}>
    <motion.div
      initial={{ width: 0 }}
      animate={{ width: `${Math.min(value, 100)}%` }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="h-full rounded-full bg-brand"
    />
  </div>
);

export const StatCard = ({ label, value, delta, icon: Icon, tone = "default", testid }) => {
  const tones = {
    default: "text-slate-900", success: "text-emerald-600", warning: "text-amber-600", danger: "text-red-600",
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</span>
        {Icon && <Icon className="h-4 w-4 text-slate-400" />}
      </div>
      <div className={`mt-3 font-mono text-2xl font-semibold ${tones[tone]}`}>{value}</div>
      {delta && <div className="mt-1 text-xs font-medium text-emerald-600">{delta}</div>}
    </div>
  );
};

export const FolioBadge = ({ folio }) => (
  <span className="inline-flex items-center rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 font-mono text-sm font-semibold tracking-tight text-slate-800" data-testid="folio-badge">
    {folio}
  </span>
);

export function Countdown({ target, className = "" }) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    const t = new Date(target).getTime();
    const tick = () => setLeft(Math.max(0, t - Date.now()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);
  const d = Math.floor(left / 86400000);
  const h = Math.floor((left % 86400000) / 3600000);
  const m = Math.floor((left % 3600000) / 60000);
  const s = Math.floor((left % 60000) / 1000);
  const Box = ({ v, l }) => (
    <div className="flex flex-col items-center">
      <span className="font-mono text-2xl font-bold tabular-nums text-slate-900 sm:text-3xl">{String(v).padStart(2, "0")}</span>
      <span className="text-[10px] uppercase tracking-wider text-slate-400">{l}</span>
    </div>
  );
  return (
    <div className={`flex items-center gap-3 ${className}`} data-testid="countdown">
      <Box v={d} l="días" /><span className="text-xl text-slate-300">:</span>
      <Box v={h} l="hrs" /><span className="text-xl text-slate-300">:</span>
      <Box v={m} l="min" /><span className="text-xl text-slate-300">:</span>
      <Box v={s} l="seg" />
    </div>
  );
}

export function MiniTimer({ target }) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    const t = new Date(target).getTime();
    const tick = () => setLeft(Math.max(0, t - Date.now()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);
  const m = Math.floor(left / 60000);
  const s = Math.floor((left % 60000) / 1000);
  const danger = left < 5 * 60000;
  return (
    <span className={`font-mono text-lg font-bold tabular-nums ${danger ? "text-red-600" : "text-amber-600"}`} data-testid="reservation-timer">
      {String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}
    </span>
  );
}

export const Stepper = ({ steps, current }) => (
  <div className="flex items-center gap-1 overflow-x-auto no-scrollbar" data-testid="stepper">
    {steps.map((s, i) => (
      <div key={s} className="flex items-center gap-1 shrink-0">
        <div className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold ${
          i < current ? "bg-emerald-50 text-emerald-700" : i === current ? "bg-brand text-white" : "bg-slate-100 text-slate-400"}`}>
          {i < current ? <CheckCircle2 className="h-3.5 w-3.5" /> : <span className="font-mono">{i + 1}</span>}
          <span className="hidden sm:inline">{s}</span>
        </div>
        {i < steps.length - 1 && <div className={`h-px w-4 ${i < current ? "bg-emerald-300" : "bg-slate-200"}`} />}
      </div>
    ))}
  </div>
);

export const Timeline = ({ items }) => (
  <ol className="relative space-y-6 border-l border-slate-200 pl-6" data-testid="timeline">
    {items.map((it, i) => (
      <li key={`${it.state}-${it.at}-${i}`} className="relative">
        <span className={`absolute -left-[31px] grid h-5 w-5 place-items-center rounded-full ring-4 ring-white ${
          it.done ? "bg-emerald-500" : it.active ? "bg-brand" : "bg-slate-300"}`}>
          {it.done ? <CheckCircle2 className="h-3 w-3 text-white" /> : <Circle className="h-2 w-2 text-white" />}
        </span>
        <div className={`text-sm font-semibold ${it.done || it.active ? "text-slate-900" : "text-slate-400"}`}>{it.label}</div>
        {it.at && <div className="text-xs text-slate-400">{it.at}</div>}
      </li>
    ))}
  </ol>
);

export const EmptyState = ({ icon: Icon = ShieldCheck, title, desc, action }) => (
  <div className="grid place-items-center rounded-xl border border-dashed border-slate-200 bg-white px-6 py-16 text-center" data-testid="empty-state">
    <Icon className="h-10 w-10 text-slate-300" />
    <h3 className="mt-4 text-lg font-semibold text-slate-800">{title}</h3>
    {desc && <p className="mt-1 max-w-sm text-sm text-slate-500">{desc}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export const Skeleton = ({ className = "" }) => (
  <div className={`animate-pulse rounded-lg bg-slate-100 ${className}`} />
);

export const NumberStat = ({ value, label }) => (
  <div>
    <div className="font-mono text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{typeof value === "number" ? num(value) : value}</div>
    <div className="mt-1 text-sm text-slate-500">{label}</div>
  </div>
);
